import React, { useEffect, useState, useMemo, useCallback } from 'react';
import db from '../db/localdb';
import { useAuth } from '../context/AuthContext';
import { createExpense, voidExpense } from '../sync/repo';
import BackButton from '../components/BackButton';
import MoneyInput from '../components/MoneyInput';
import { fmtDate } from '../utils/date';

const CATEGORIES = ['general', 'rent', 'salaries', 'utilities', 'marketing', 'transport', 'maintenance', 'other'];

export default function Expenses() {
  const { business } = useAuth();
  const bid = business?.id;

  const [allExpenses, setAllExpenses] = useState([]);
  const [showForm, setShowForm]       = useState(false);
  const [filter, setFilter]           = useState('');
  const [saving, setSaving]           = useState(false);
  const [formError, setFormError]     = useState('');
  const [form, setForm] = useState({ category: 'general', amount: '', description: '' });

  const loadExpenses = useCallback(async () => {
    if (!bid) return;
    const rows = await db.expenses
      .where('business_id').equals(bid)
      .filter(e => !e.voided_at)
      .toArray();
    rows.sort((a, b) => new Date(b.expense_at || b.created_at) - new Date(a.expense_at || a.created_at));
    setAllExpenses(rows);
  }, [bid]);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);

  const expenses = useMemo(() => {
    if (!filter) return allExpenses;
    return allExpenses.filter(e => e.category === filter);
  }, [allExpenses, filter]);

  const total = useMemo(
    () => allExpenses.reduce((s, e) => s + Number(e.amount), 0),
    [allExpenses]
  );

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const cur = business?.currency || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { setFormError('Enter a valid positive amount'); return; }
    setSaving(true);
    setFormError('');
    try {
      await createExpense({ ...form, amount, business_id: bid });
      setForm({ category: 'general', amount: '', description: '' });
      setShowForm(false);
      await loadExpenses();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async (id) => {
    if (!window.confirm('Remove this expense?')) return;
    try {
      await voidExpense(id);
      await loadExpenses();
    } catch { alert('Failed to remove expense'); }
  };

  return (
    <div className="page">
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Expenses</h1>
        <button className="qa-btn" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>
      <p className="text-muted" style={{ marginBottom: '1rem' }}>{cur} · {business?.name}</p>

      {/* Total expenses */}
      <div className="card" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--color-muted)', fontSize: '0.9rem' }}>Total Expenses</span>
        <span style={{ fontWeight: 700, color: 'var(--color-danger)' }}>{cur} {fmt(total)}</span>
      </div>

      {/* Add expense form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="card" style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>Category</label>
            <select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              style={{ width: '100%', marginTop: '0.25rem' }}
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>Amount ({cur})</label>
            <MoneyInput
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="0.00"
              style={{ width: '100%', marginTop: '0.25rem' }}
              required
            />
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)' }}>Description (optional)</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. January rent"
              style={{ width: '100%', marginTop: '0.25rem' }}
            />
          </div>
          {formError && <p style={{ color: 'var(--color-danger)', fontSize: '0.85rem', margin: 0 }}>{formError}</p>}
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Add Expense'}
          </button>
        </form>
      )}

      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', marginBottom: '0.75rem', paddingBottom: '0.25rem' }}>
        {['', ...CATEGORIES].map(c => (
          <button key={c || 'all'}
            onClick={() => setFilter(c)}
            style={{ whiteSpace: 'nowrap', padding: '0.3rem 0.75rem', borderRadius: '999px',
              border: '1px solid var(--color-border)',
              background: filter === c ? 'var(--color-primary)' : 'transparent',
              color: filter === c ? '#fff' : 'inherit', cursor: 'pointer', fontSize: '0.8rem' }}
          >
            {c ? c.charAt(0).toUpperCase() + c.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {/* Expense list */}
      {expenses.length === 0 && (
        <p className="text-muted">No expenses{filter ? ` in "${filter}"` : ''} yet.</p>
      )}
      {expenses.map(exp => (
        <div key={exp.id} className="card" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{cur} {fmt(exp.amount)}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>
              {exp.category}{exp.description ? ` · ${exp.description}` : ''} · {fmtDate(exp.expense_at || exp.created_at)}
            </div>
          </div>
          <button
            onClick={() => handleVoid(exp.id)}
            style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: '0.8rem' }}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}
